import { FC, ReactNode, createContext, useCallback, useState } from 'react';

import { AuthUserSession } from '@/modules/Auth';

interface AuthContextProps {
  sessionUser: AuthUserSession | null | undefined;
  isAuthed: boolean;
  setSessionUser: (user: AuthUserSession | null) => void;
  setAuthingState: (isAuthed: boolean) => void;
  changeCurrentTeamId: (teamId: string | null) => void;
}

export const AuthContext = createContext<AuthContextProps>({
  sessionUser: null,
  isAuthed: false,
  setSessionUser: () => {},
  setAuthingState: () => {},
  changeCurrentTeamId: () => {},
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionUser, setUser] = useState<AuthContextProps['sessionUser']>(undefined);
  const [isAuthed, setIsAuthed] = useState<AuthContextProps['isAuthed']>(false);

  const setAuthingState: AuthContextProps['setAuthingState'] = useCallback((isAuthedValue) => {
    setIsAuthed(isAuthedValue);
  }, []);

  const setSessionUser: AuthContextProps['setSessionUser'] = useCallback((user) => {
    setUser(user);
  }, []);

  const changeCurrentTeamId = useCallback((teamId: string | null) => {
    localStorage.setItem('currentTeamId', teamId || 'null');
    setUser((prevUser) => (prevUser ? { ...prevUser, currentTeamId: teamId } : prevUser));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthed,
        sessionUser,
        setSessionUser,
        setAuthingState,
        changeCurrentTeamId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
